import type { RefObject } from 'react';

/** Selector for Menu dropdown panel (used by AuditTimestampFilter). */
export const OUTSIDE_CLICK_SELECTORS = {
  menuDropdown: '[class*="Menu-dropdown"]',
  reactDatepicker: '.react-datepicker',
  menuMultiDropdown: '[class*="MenuMulti-dropdown"]',
} as const;

/**
 * Returns true if the click target is inside the container or inside any element
 * matching one of the ignore selectors (e.g. portaled dropdown or calendar).
 * Safe for non-Element targets (e.g. text nodes): uses parentElement for .closest().
 *
 * @param target - The event target node (may be a text node).
 * @param containerRef - Ref to the wrapper element (e.g. filter pill container).
 * @param ignoreSelectors - CSS selectors for elements that should not trigger close.
 * @returns True if the click should be treated as "inside" (do not close).
 */
export function isClickInsideArea(
  target: Node,
  containerRef: RefObject<HTMLDivElement | null>,
  ignoreSelectors: readonly string[]
): boolean {
  const container = containerRef.current;

  if (
    container &&
    typeof container.contains === 'function' &&
    container.contains(target)
  ) {
    return true;
  }

  // Only Element nodes have .closest(); text nodes and others will throw.
  const el = target instanceof Element ? target : target.parentElement ?? null;

  if (!el) {
    return false;
  }

  return ignoreSelectors.some((selector) => el.closest(selector));
}
